/**
 * As we noted for utils/color-modifier.ts, we will keep design defined value
 * alongside modifier values for backward compatibility.
 */

export const COLOR_PREFIX = "color";
export const rawColors = {
  carbon50: "#F6FAFF",
  carbon100: "#F0F7FF",
  carbon200: "#E9F4FF",
  carbon300: "#CDDCEB",
  carbon400: "#AABCCE",
  carbon500: "#8A9DB1",
  carbon600: "#6C8094",
  carbon600Fade050: "rgba(108, 128, 148, 0.5)", // Specific case in graphs - corresponds to carbon600 with transparency of 0.5
  carbon700: "#516476",
  carbon800: "#394959",
  carbon800Fade20: "rgba(57, 73, 89, 0.2)",
  carbon900: "#11181F",
  carbon900Fade90: "rgba(17, 24, 31, 0.1)", // Specific case in scrollbar - corresponds to carbon900 with transparency of 0.1
  chlorine50: "#F6FFE9",
  chlorine100: "#E6FFC2",
  chlorine200: "#D7FF9C",
  chlorine300: "#C7FF75",
  chlorine400: "#B8FF4E",
  chlorine500: "#A8FF27",
  chlorine600: "#88D910",
  chlorine700: "#69B000",
  chlorine800: "#518700",
  chlorine900: "#395F00",
  interGroupArea: "rgba(233, 235, 241, 0.4)", // Specific case in graph - corresponds to titanium100 with transparency of 0.4
  darkNitrogen50: "#EEF2FF",
  darkNitrogen100: "#CFDAFF",
  darkNitrogen200: "#B1C3FF",
  darkNitrogen300: "#92ABFF",
  darkNitrogen400: "#7494FF",
  darkNitrogen500: "#4E72EA",
  darkNitrogen500Fade080: "rgba(78, 114, 234, 0.2)", // Specific case in navigator - corresponds to darkNitrogen with transparency of 0.2
  darkNitrogen600: "#2B4EC2",
  darkNitrogen700: "#113199",
  darkNitrogen800: "#001A70",
  darkNitrogen900: "#001147",
  hydrogen: "#FFFFFF",
  hydrogenFade90: "rgba(255, 255, 255, 0.1)", // Specific case in scrollbar - corresponds to hydrogen with transparency of 0.1
  hydrogenFade30: "rgba(255, 255, 255, 0.7)", // Specific case in scrollbar - corresponds to hydrogen with transparency of 0.1
  lightNitrogen50: "#E7F3FF",
  lightNitrogen100: "#BCDEFF",
  lightNitrogen100Fade050: "rgba(188, 222, 255, 0.5)", // Specific case in graphs - corresponds to lightNitrogen100 with transparency of 0.5
  lightNitrogen200: "#91C9FF",
  lightNitrogen300: "#66B3FF",
  lightNitrogen400: "#3B9EFF",
  lightNitrogen500_Main: "#1089FF",
  lightNitrogen500_MainFade075: "rgba(16, 137, 255, 0.25)",
  lightNitrogen500_MainFade060: "rgba(16, 137, 255, 0.40)",
  lightNitrogen600: "#006DD6",
  lightNitrogen700: "#0058AD",
  lightNitrogen800: "#004385",
  lightNitrogen900: "#002F5C",
  modalOverlayBackground: "rgba(17, 24, 31, 0.4)", // Specific case of the modal - corresponds to carbon900 with opacity of 0.4
  oxygen50: "#FFECEB",
  oxygen100: "#FFC3BF",
  oxygen200: "#FF9A93",
  oxygen300: "#FF7167",
  oxygen400: "#ED4337",
  oxygen500: "#CB2F24",
  oxygen600: "#A91F15",
  oxygen700: "#87120A",
  oxygen800: "#650902",
  oxygen900: "#430400",
  phosphorus50: "#FFF5EB",
  phosphorus100: "#FFDEBE",
  phosphorus200: "#FFC891",
  phosphorus300: "#FFB165",
  phosphorus400: "#FF9B38",
  phosphorus500: "#DD8125",
  phosphorus600: "#BB6816",
  phosphorus700: "#99510A",
  phosphorus800: "#773C01",
  phosphorus900: "#552A00",
  titanium50: "#FAFBFF",
  titanium100: "#E9EBF1",
  titanium200: "#D2D4DD",
  titanium300: "#BBBEC8",
  titanium400: "#A5A9B4",
  titanium500: "#90949F",
  titanium600: "#7B7F8B",
  titanium700: "#676B77",
  titanium800: "#545862",
  titanium900: "#41444E",
  shadowColor: "rgba(65, 68, 78, 0.1)",
  zz_headerBackground1: "#142433", // To remove when header is removed
  zz_headerBackground2: "#192D40", // To remove when header is removed
  zz_tooltipBackground: "#414141", // To remove when tooltip is removed
  zz_tooltipBorder: "#222222", // To remove when tooltip is removed
  // Chart
  chart_light_nitrogen_600: "#006DD6",
  chart_grenache: "#9F1853",
  chart_sarcelle: "#005D5D",
  chart_phosphorus_600: "#BB6816",
  chart_prune: "#6929C4",
  chart_turquoise: "#009D9A",
  chart_chlorine_800: "#518700",
  chart_dark_nitrogen_700: "#113199",
  chart_chocolat: "#570408",
  chart_moutarde: "#A99211",
  chart_categorical_blue: "#1F77B4",
  chart_fade_categorical_blue: "#BCDDFFB3",
  // Categorical colors for matrix formatting
  chart_categorical_green: "#2CA02C",
  chart_categorical_orange: "#FF7F0E",
  chart_categorical_red: "#D62728",
};

export const semanticColor = {
  success: "chlorine600",
  successBackground: "chlorine50",
  warning: "phosphorus400",
  warningBackground: "phosphorus50",
  error: "oxygen400",
  errorBackground: "oxygen50",
  info: "darkNitrogen600",
  infoBackground: "darkNitrogen50",
  primary: "lightNitrogen500_Main",
  suppressive: "oxygen400",
} as const;

export const rawSemanticColors = {
  success: rawColors[semanticColor.success],
  successBackground: rawColors[semanticColor.successBackground],
  warning: rawColors[semanticColor.warning],
  warningBackground: rawColors[semanticColor.warningBackground],
  error: rawColors[semanticColor.error],
  errorBackground: rawColors[semanticColor.errorBackground],
  info: rawColors[semanticColor.info],
  infoBackground: rawColors[semanticColor.infoBackground],
  primary: rawColors[semanticColor.primary],
  suppressive: rawColors[semanticColor.suppressive],
};

export type Colors = {
  [key in keyof typeof rawColors | keyof typeof rawSemanticColors]: string;
} & {
  raw: Record<keyof typeof rawColors | keyof typeof rawSemanticColors, string>;
};
